import { createClient, SupabaseClient } from '@supabase/supabase-js';

// Supabase configuration - Public keys are safe when RLS is properly configured
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Please check your .env file.');
}

export const supabase: SupabaseClient<Database> = createClient(supabaseUrl, supabaseAnonKey);

// Note: We don't expose service_role key in frontend for security reasons
// Instead, we use edge functions for operations requiring elevated permissions

// Database Types
export interface Database {
  public: {
    Tables: {
      users: {
        Row: {
          id: string;
          email: string;
          role: 'artist' | 'admin';
          created_at: string;
        };
        Insert: {
          id?: string;
          email: string;
          role: 'artist' | 'admin';
          created_at?: string;
        };
        Update: {
          id?: string;
          email?: string;
          role?: 'artist' | 'admin';
          created_at?: string;
        };
      };
      artists: {
        Row: {
          id: string;
          user_id: string;
          first_name: string;
          last_name: string;
          email: string;
          display_name: string;
          bio?: string;
          avatar?: string;
          created_at: string;
          status: 'active' | 'inactive';
        };
        Insert: {
          id?: string;
          user_id: string;
          first_name: string;
          last_name: string;
          email: string;
          display_name: string;
          bio?: string;
          avatar?: string;
          created_at?: string;
          status?: 'active' | 'inactive';
        };
        Update: {
          id?: string;
          user_id?: string;
          first_name?: string;
          last_name?: string;
          email?: string;
          display_name?: string;
          bio?: string;
          avatar?: string;
          created_at?: string;
          status?: 'active' | 'inactive';
        };
      };
      releases: {
        Row: {
          id: string;
          artist_id: string;
          title: string;
          type: 'Single' | 'EP' | 'Album';
          release_date: string;
          genre: string;
          subgenre: string;
          language: string;
          artist_display_name: string;
          status: 'draft' | 'pending' | 'approved' | 'rejected' | 'submitted' | 'published';
          cover_art?: string;
          s3_cover_key?: string;
          cover_storage_type?: string;
          description?: string;
          concept_description?: string;
          created_at: string;
        };
        Insert: {
          id?: string;
          artist_id: string;
          title: string;
          type: 'Single' | 'EP' | 'Album';
          release_date: string;
          genre: string;
          subgenre: string;
          language: string;
          artist_display_name: string;
          status?: 'draft' | 'pending' | 'approved' | 'rejected' | 'submitted' | 'published';
          cover_art?: string;
          s3_cover_key?: string;
          cover_storage_type?: string;
          description?: string;
          concept_description?: string;
          created_at?: string;
        };
        Update: {
          id?: string;
          artist_id?: string;
          title?: string;
          type?: 'Single' | 'EP' | 'Album';
          release_date?: string;
          genre?: string;
          subgenre?: string;
          language?: string;
          artist_display_name?: string;
          status?: 'draft' | 'pending' | 'approved' | 'rejected' | 'submitted' | 'published';
          cover_art?: string;
          s3_cover_key?: string;
          cover_storage_type?: string;
          description?: string;
          concept_description?: string;
          created_at?: string;
        };
      };
      tracks: {
        Row: {
          id: string;
          release_id: string;
          title: string;
          duration: string;
          order: number;
          track_number?: number;
          is_explicit: boolean;
          language: string;
          has_lyrics: boolean;
          lyrics?: string;
          audio_file?: string;
          audio_file_id?: string;
          s3_audio_key?: string;
          audio_storage_type?: string;
          ai_generated: {
            music: boolean;
            lyrics: boolean;
            vocals: boolean;
            human_contribution: boolean;
            human_notes: string;
          };
        };
        Insert: {
          id?: string;
          release_id: string;
          title: string;
          duration: string;
          order: number;
          track_number?: number;
          is_explicit: boolean;
          language: string;
          has_lyrics: boolean;
          lyrics?: string;
          audio_file?: string;
          audio_file_id?: string;
          s3_audio_key?: string;
          audio_storage_type?: string;
          ai_generated: {
            music: boolean;
            lyrics: boolean;
            vocals: boolean;
            human_contribution: boolean;
            human_notes: string;
          };
        };
        Update: {
          id?: string;
          release_id?: string;
          title?: string;
          duration?: string;
          order?: number;
          track_number?: number;
          is_explicit?: boolean;
          language?: string;
          has_lyrics?: boolean;
          lyrics?: string;
          audio_file?: string;
          audio_file_id?: string;
          s3_audio_key?: string;
          audio_storage_type?: string;
          ai_generated?: {
            music: boolean;
            lyrics: boolean;
            vocals: boolean;
            human_contribution: boolean;
            human_notes: string;
          };
        };
      };
      rights: {
        Row: {
          id: string;
          release_id: string;
          isrc: string;
          upc: string;
          phonogram_owner: string;
          composition_owner: string;
        };
        Insert: {
          id?: string;
          release_id: string;
          isrc: string;
          upc: string;
          phonogram_owner: string;
          composition_owner: string;
        };
        Update: {
          id?: string;
          release_id?: string;
          isrc?: string;
          upc?: string;
          phonogram_owner?: string;
          composition_owner?: string;
        };
      };
      contributors: {
        Row: {
          id: string;
          release_id: string;
          name: string;
          role: 'composer' | 'producer' | 'mastering' | 'lyricist';
        };
        Insert: {
          id?: string;
          release_id: string;
          name: string;
          role: 'composer' | 'producer' | 'mastering' | 'lyricist';
        };
        Update: {
          id?: string;
          release_id?: string;
          name?: string;
          role?: 'composer' | 'producer' | 'mastering' | 'lyricist';
        };
      };
      earnings: {
        Row: {
          id: string;
          user_id: string;
          month: string;
          platform: string;
          track_name: string;
          streams: number;
          revenue: string;
          payment_status: 'pending' | 'paid' | 'processing';
          payment_date?: string;
        };
        Insert: {
          id?: string;
          user_id: string;
          month: string;
          platform: string;
          track_name: string;
          streams: number;
          revenue: string;
          payment_status: 'pending' | 'paid' | 'processing';
          payment_date?: string;
        };
        Update: {
          id?: string;
          user_id?: string;
          month?: string;
          platform?: string;
          track_name?: string;
          streams?: number;
          revenue?: string;
          payment_status?: 'pending' | 'paid' | 'processing';
          payment_date?: string;
        };
      };
      payout_info: {
        Row: {
          id: string;
          user_id: string;
          type: 'paypal' | 'bank';
          paypal_email?: string;
          iban?: string;
          full_name: string;
          address: string;
          city: string;
          postal_code: string;
          country: string;
          currency: 'TRY' | 'USD' | 'EUR';
        };
        Insert: {
          id?: string;
          user_id: string;
          type: 'paypal' | 'bank';
          paypal_email?: string;
          iban?: string;
          full_name: string;
          address: string;
          city: string;
          postal_code: string;
          country: string;
          currency: 'TRY' | 'USD' | 'EUR';
        };
        Update: {
          id?: string;
          user_id?: string;
          type?: 'paypal' | 'bank';
          paypal_email?: string;
          iban?: string;
          full_name?: string;
          address?: string;
          city?: string;
          postal_code?: string;
          country?: string;
          currency?: 'TRY' | 'USD' | 'EUR';
        };
      };
      paypal_info: {
        Row: {
          id: string;
          user_id: string;
          full_name: string;
          paypal_email: string;
          is_verified: boolean;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          user_id: string;
          full_name: string;
          paypal_email: string;
          is_verified?: boolean;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          user_id?: string;
          full_name?: string;
          paypal_email?: string;
          is_verified?: boolean;
          created_at?: string;
          updated_at?: string;
        };
      };
    };
  };
} 